import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { ConfirmDialog, PromptDialog } from '../../components/Dialogs';
import {
  SetupTaskInterface,
  PrintingTaskInterface,
  CuttingTaskInterface,
  SeamedTaskInterface,
  QualityCheckTaskInterface,
  MaterialPrepTaskInterface
} from '../../components/Tasks/TypeSpecific';
import { useTaskTypes } from '../../contexts/TaskTypesContext';
import ShiftHandoffModal from '../../components/Tasks/ShiftHandoffModal';
import './HomePage.css';
import { formatAppDate } from '../../utils/dateUtils';

const OperatorDashboard = () => {
  const { t } = useTranslation('operator');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();
  const { getProductionTaskTypes, getTaskTypeConfig, taskTypeMap } = useTaskTypes();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'materials'
  const [taskFilter, setTaskFilter] = useState('my-tasks'); // 'my-tasks', 'available', 'completed-today'
  const [myTasks, setMyTasks] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [completedToday, setCompletedToday] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null); // For showing task details
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [workflowProgress, setWorkflowProgress] = useState([]); // For breadcrumb display
  const [stats, setStats] = useState({
    tasksCompleted: 0,
    tasksInProgress: 0,
    avgCompletionTime: 0,
    qualityScore: 0,
    todayCompleted: 0,
    weekCompleted: 0,
    efficiency: 0,
    utilizationRate: 0
  });

  // Shift handoff modal state
  const [showShiftHandoffModal, setShowShiftHandoffModal] = useState(false);

  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeTimeLogId, setActiveTimeLogId] = useState(null);

  // Production task types that require quantity tracking (from DB)
  const PRODUCTION_TASK_TYPES = getProductionTaskTypes();

  const [taskTimeLogs, setTaskTimeLogs] = useState([]);
  const [taskTimeSummary, setTaskTimeSummary] = useState(null);
  const intervalRef = useRef(null);

  // Material requisition state
  const [showRequisitionModal, setShowRequisitionModal] = useState(false);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [requisitionItems, setRequisitionItems] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [validatingQuantities, setValidatingQuantities] = useState(false);
  const [showOverBomDialog, setShowOverBomDialog] = useState(false);
  const [overBomReason, setOverBomReason] = useState('');

  // Materials received state
  const [receivedMaterials, setReceivedMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Material action selector state
  const [showMaterialActionModal, setShowMaterialActionModal] = useState(false);

  // Material return state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [issuedMaterials, setIssuedMaterials] = useState([]);
  const [returnItems, setReturnItems] = useState([]);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const submittingReturnRef = useRef(false); // a double click fires twice before the disabled button re-renders

  // Material scrap state
  const [showScrapModal, setShowScrapModal] = useState(false);
  const [scrapItems, setScrapItems] = useState([]);

  // Task materials summary panel state
  const [taskMaterials, setTaskMaterials] = useState({}); // keyed by task_id
  const [taskMaterialsExpanded, setTaskMaterialsExpanded] = useState(false);
  const [taskMaterialsLoading, setTaskMaterialsLoading] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [promptDialog, setPromptDialog] = useState({ show: false, title: '', message: '', fields: [], onSubmit: null });

  // Reset materials panel when selected task changes
  useEffect(() => {
    setTaskMaterialsExpanded(false);
    setTaskMaterialsLoading(false);
  }, [selectedTask?.task_id]);

  const fetchTaskMaterials = async (taskId) => {
    if (taskMaterials[taskId]) return; // already cached
    setTaskMaterialsLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${taskId}/materials-summary`));
      if (response.ok) {
        const data = await response.json();
        setTaskMaterials(prev => ({ ...prev, [taskId]: data.materials || [] }));
      }
    } catch (err) {
      console.error('Error fetching task materials summary:', err);
    } finally {
      setTaskMaterialsLoading(false);
    }
  };

  const refreshTaskMaterials = async (taskId) => {
    setTaskMaterialsLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${taskId}/materials-summary`));
      if (response.ok) {
        const data = await response.json();
        setTaskMaterials(prev => ({ ...prev, [taskId]: data.materials || [] }));
      }
    } catch (err) {
      console.error('Error refreshing task materials summary:', err);
    } finally {
      setTaskMaterialsLoading(false);
    }
  };

  const getMaterialStatusStyle = (status) => {
    switch (status) {
      case 'NONE': return { background: '#6c757d', color: 'white' };
      case 'PENDING': return { background: '#ffc107', color: '#333' };
      case 'PARTIAL': return { background: '#fd7e14', color: 'white' };
      case 'FULFILLED': return { background: '#28a745', color: 'white' };
      case 'OVER': return { background: '#dc3545', color: 'white' };
      default: return { background: '#6c757d', color: 'white' };
    }
  };

  // Helper functions for material requisition status display
  const getRequisitionStatusClass = (status) => {
    switch (status) {
      case 'PENDING': return 'status-warning';
      case 'APPROVED': return 'status-info';
      case 'REJECTED': return 'status-error';
      case 'PICKED': return 'status-info';
      case 'ISSUED': return 'status-success';
      case 'CANCELLED': return 'status-error';
      case 'DRAFT': return 'status-secondary';
      default: return 'status-secondary';
    }
  };

  const getRequisitionStatusText = (status) => {
    switch (status) {
      case 'PENDING': return t('taskStatus.pending');
      case 'APPROVED': return t('taskStatus.approved');
      case 'REJECTED': return t('taskStatus.rejected');
      case 'PICKED': return t('taskStatus.picked');
      case 'ISSUED': return t('taskStatus.issued');
      case 'CANCELLED': return t('taskStatus.cancelled');
      case 'DRAFT': return t('taskStatus.draft');
      default: return status;
    }
  };

  // Label for the date of the requisition's last status change
  const getStatusDateLabel = (status) => {
    switch (status) {
      case 'ISSUED': return t('operatorDashboard.materialsTab.delivered');
      case 'APPROVED': return t('common:ui.approved');
      case 'REJECTED': return t('common:ui.rejected');
      default: return t('operatorDashboard.materialsTab.statusChanged');
    }
  };

  useEffect(() => {
    fetchOperatorDashboard();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'materials') {
      fetchReceivedMaterials();
    }
  }, [activeTab]);

  useEffect(() => {
    // Update filtered tasks when filter changes
    switch (taskFilter) {
      case 'available':
        setFilteredTasks(availableTasks);
        break;
      case 'completed-today':
        setFilteredTasks(completedToday);
        break;
      case 'my-tasks':
      default:
        setFilteredTasks(myTasks);
        break;
    }
  }, [taskFilter, myTasks, availableTasks, completedToday]);

  // Timer effect
  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timerRunning]);

  // Initialize timer when active task changes
  useEffect(() => {
    if (activeTask?.actual_start) {
      const startTime = new Date(activeTask.actual_start);
      const now = new Date();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);
      setTimerRunning(true);
    } else {
      setElapsedTime(0);
      setTimerRunning(false);
    }
  }, [activeTask]);

  const fetchOperatorDashboard = async () => {
    if (!user?.user_id) return;

    try {
      setLoading(true);

      // Fetch my tasks (including completed ones)
      const myTasksResponse = await apiFetch(getApiUrl(`/api/production-tasks/users/${user.user_id}/tasks?include_completed=true`));
      if (myTasksResponse.ok) {
        const tasks = await myTasksResponse.json();
        const activeTasks = tasks.filter(t =>
          t.status !== 'COMPLETED' &&
          t.status !== 'CANCELLED' &&
          !(t.status === 'PAUSED' && t.assigned_to !== user?.user_id)
        );
        const inProgressTask = activeTasks.find(t => t.status === 'IN_PROGRESS');

        // Filter today's completed tasks
        const todayTasks = tasks.filter(t => {
          if (t.status !== 'COMPLETED') return false;
          // Check if completed today
          const completedDate = new Date(t.updated_at || t.actual_end);
          const today = new Date();
          return completedDate.toDateString() === today.toDateString();
        });

        // Calculate week's completed tasks
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const weekTasks = tasks.filter(t => {
          if (t.status !== 'COMPLETED') return false;
          const completedDate = new Date(t.actual_end || t.updated_at);
          return completedDate >= weekAgo;
        });

        setMyTasks(activeTasks);
        setActiveTask(inProgressTask);
        setCompletedToday(todayTasks);

        // Calculate stats
        const completed = tasks.filter(t => t.status === 'COMPLETED').length;
        const inProgress = activeTasks.filter(t => t.status === 'IN_PROGRESS').length;
        const avgTime = tasks
          .filter(t => t.actual_duration_minutes)
          .reduce((sum, t) => sum + t.actual_duration_minutes, 0) / (completed || 1);

        // Calculate efficiency (actual vs estimated time)
        const completedWithTime = tasks.filter(t =>
          t.status === 'COMPLETED' && t.actual_duration_minutes && t.estimated_duration_minutes
        );
        const efficiency = completedWithTime.length > 0
          ? Math.round((completedWithTime.reduce((sum, t) =>
              sum + (t.estimated_duration_minutes / t.actual_duration_minutes), 0
            ) / completedWithTime.length) * 100)
          : 100;

        setStats({
          tasksCompleted: completed,
          tasksInProgress: inProgress,
          avgCompletionTime: Math.round(avgTime),
          qualityScore: 95, // This would come from quality ratings
          todayCompleted: todayTasks.length,
          weekCompleted: weekTasks.length,
          efficiency: Math.min(efficiency, 100),
          utilizationRate: Math.round((inProgress > 0 ? 100 : (activeTasks.length > 0 ? 50 : 0)))
        });
      }

      // Fetch available tasks (unassigned in my department)
      const availableResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks?department_id=${user.department_id}`));
      if (availableResponse.ok) {
        const tasks = await availableResponse.json();
        const unassigned = tasks.filter(t => !t.assigned_to && t.status === 'PENDING');
        setAvailableTasks(unassigned);
      }
    } catch (error) {
      console.error('Error fetching operator dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTask = async (taskId) => {
    try {

      // First, start the time log
      const timeLogResponse = await apiFetch(getApiUrl(`/api/production-tasks/time-logs/start?task_id=${taskId}&activity_type=WORK`), {
        method: 'POST'
      });

      if (timeLogResponse.ok) {
        const timeLogData = await timeLogResponse.json();
        setActiveTimeLogId(timeLogData.log_id);
        setElapsedTime(0);
        setTimerRunning(true);
      }

      // Then update task status to IN_PROGRESS
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/status?status=IN_PROGRESS`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        // Update selectedTask if it's the same task
        if (selectedTask && selectedTask.task_id === taskId) {
          setSelectedTask({...selectedTask, status: 'IN_PROGRESS'});
        }
        fetchOperatorDashboard();
      }
    } catch (error) {
      console.error('Error starting task:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    const taskInfo = selectedTask || myTasks.find(t => t.task_id === taskId);

    // Fetch previous shift completion logs for this task
    let previousLogs = [];
    let previousTotal = 0;
    try {
      const logsRes = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/completion-logs`));
      if (logsRes.ok) {
        previousLogs = await logsRes.json();
        previousTotal = previousLogs.reduce((sum, l) => sum + (parseFloat(l.quantity_produced) || 0), 0);
      }
    } catch (e) {
      console.error('Error fetching completion logs:', e);
    }

    // Determine the UOM for this task type (use previous logs first, fall back to task-type default)
    const uomByTaskType = { DIE_CUTTING: 'MILLARES', SEAMED: 'ROLLS' };
    const taskUom = (previousLogs.length > 0 && previousLogs[0].uom)
      ? previousLogs[0].uom
      : (uomByTaskType[taskInfo?.task_type] || 'PCS');

    // Build dialog message
    const ct = (key, opts) => t(`operator:dialogs.completeTask.${key}`, opts);
    let detailedMessage = `${ct('infoTask')}: ${taskInfo?.task_name || '-'}\n${ct('infoProduct')}: ${taskInfo?.product_name || 'N/A'}`;
    if (taskInfo?.po_number) detailedMessage += `\n${ct('infoPO')}: ${taskInfo.po_number}`;
    if (taskInfo?.customer_name) detailedMessage += `\n${ct('infoCustomer')}: ${taskInfo.customer_name}`;

    if (previousLogs.length > 0) {
      detailedMessage += `\n\n${ct('previousShiftsHeader')}`;
      previousLogs.forEach((l, i) => {
        const qty = parseFloat(l.quantity_produced || 0).toFixed(2);
        const who = l.operator_name || l.username || ct('shiftLabel', { n: i + 1 });
        detailedMessage += `\n${who}: ${qty} ${l.uom || taskUom}`;
      });
      detailedMessage += `\n${ct('accumulatedSoFar', { total: previousTotal.toFixed(2), uom: taskUom })}`;
      detailedMessage += `\n\n${ct('enterYourShift')}`;
    } else {
      detailedMessage += `\n\n${ct('enterFirstShift')}`;
    }

    const fields = [
      {
        name: 'shiftType',
        label: t('operator:labels.shiftType', { defaultValue: 'Tipo de Turno' }),
        type: 'select',
        required: true,
        options: ['HD', 'HED', 'HN', 'HE', 'HEB', 'HEN'].map(value => ({
          value,
          label: t(`operatorDashboard.shiftTypes.${value}`)
        }))
      },
      {
        name: 'quantityProduced',
        label: ct('quantityLabel', { uom: taskUom }),
        type: 'number',
        required: true,
        min: 0,
        step: 0.01,
        placeholder: ct('quantityPlaceholder')
      },
      {
        name: 'notes',
        label: ct('notesLabel'),
        type: 'text',
        required: false
      },
      {
        name: 'wasteQuantityKg',
        label: ct('wasteLabel'),
        type: 'number',
        required: false,
        min: 0,
        step: 0.001,
        placeholder: t('operator:placeholders.wasteQuantity')
      },
      {
        name: 'wasteNotes',
        label: t('operator:labels.wasteDescription'),
        type: 'text',
        required: false,
        placeholder: t('operator:placeholders.wasteDescription')
      }
    ];

    setPromptDialog({
      show: true,
      title: t('dialogs.completeTask.title'),
      message: detailedMessage,
      fields: fields,
      onSubmit: async (values) => {

        const quantityProduced = parseFloat(values.quantityProduced);
        if (!quantityProduced || quantityProduced < 0) {
          showError(t('errors.generic.error'), t('operatorDashboard.errors.invalidQuantity'));
          return;
        }

        try {
          // End the current time log if there is one
          if (activeTimeLogId) {
            await apiFetch(getApiUrl(`/api/production-tasks/time-logs/${activeTimeLogId}/end`), {
              method: 'PATCH'
            });
            setActiveTimeLogId(null);
            setTimerRunning(false);
          }

          const payload = {
            quantity_produced: quantityProduced,
            uom: taskUom,
            completion_notes: values.notes || 'Task completed by operator',
            waste_quantity_kg: values.wasteQuantityKg ? parseFloat(values.wasteQuantityKg) : null,
            waste_notes: values.wasteNotes || null,
            shift_type: values.shiftType || 'HD'
          };

          // Use the /complete endpoint — records the shift log and sums all shifts into approved_quantity
          const response = await apiFetch(
            getApiUrl(`/api/production-tasks/tasks/${taskId}/complete`),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            }
          );

          if (response.ok) {
            const total = previousTotal + quantityProduced;
            showSuccess(
              t('success.task.success'),
              ct('successMessage', { total: total.toFixed(2), uom: taskUom, shifts: previousLogs.length + 1 })
            );
            fetchOperatorDashboard();
            setTimeout(() => setSelectedTask(null), 1500);
          } else {
            const err = await response.json().catch(() => ({}));
            showError(t('errors.generic.error'), err.detail || err.message || t('errors.taskCompletion.failed'));
          }
        } catch (error) {
          console.error('Error completing task:', error);
          showError(t('errors.generic.error'), t('errors.taskCompletion.failed'));
        }
      }
    });
  };

  const handleAcceptTask = async (taskId) => {
    try {
      const response = await apiFetch(getApiUrl('/api/production-tasks/assignments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: taskId,
          assigned_to: user.user_id,
          assigned_by: user.user_id
        })
      });

      if (response.ok) {
        fetchOperatorDashboard();
      }
    } catch (error) {
      console.error('Error accepting task:', error);
    }
  };

  // Task action handlers
  const handlePauseTask = async (taskId) => {
    try {

      // End the current time log if there is one
      if (activeTimeLogId) {
        await apiFetch(getApiUrl(`/api/production-tasks/time-logs/${activeTimeLogId}/end`), {
          method: 'PATCH'
        });
        setActiveTimeLogId(null);
        setTimerRunning(false);
      }

      // Update task status to PAUSED
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/status?status=PAUSED`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        // Update selectedTask if it's the same task
        if (selectedTask && selectedTask.task_id === taskId) {
          setSelectedTask({...selectedTask, status: 'PAUSED'});
        }
        fetchOperatorDashboard();
      }
    } catch (error) {
      console.error('Error pausing task:', error);
    }
  };

  const handleResumeTask = async (taskId) => {
    try {

      // Start a new time log entry for the resumed task
      const timeLogResponse = await apiFetch(getApiUrl(`/api/production-tasks/time-logs/start?task_id=${taskId}&activity_type=WORK`), {
        method: 'POST'
      });

      if (timeLogResponse.ok) {
        const timeLogData = await timeLogResponse.json();
        setActiveTimeLogId(timeLogData.log_id);
        setElapsedTime(0);
        setTimerRunning(true);
      }

      // Update task status to IN_PROGRESS
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/status?status=IN_PROGRESS`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        // Update selectedTask if it's the same task
        if (selectedTask && selectedTask.task_id === taskId) {
          setSelectedTask({...selectedTask, status: 'IN_PROGRESS'});
        }
        fetchOperatorDashboard();
      }
    } catch (error) {
      console.error('Error resuming task:', error);
    }
  };

  // [backend value, label key in operator:dialogs.reportIssue]
  const issueTypeOptions = [['MATERIAL', 'materialShortage'], ['MACHINE', 'machineMalfunction'], ['QUALITY', 'qualityDefect'], ['SAFETY', 'safetyConcern'], ['OTHER', 'other']];
  const severityOptions = [['LOW', 'low'], ['MEDIUM', 'medium'], ['HIGH', 'high'], ['CRITICAL', 'critical']];

  const handleReportIssue = (taskId) => {
    setPromptDialog({
      show: true,
      title: t('dialogs.reportIssue.title'),
      message: t('operatorDashboard.reportIssue.message'),
      fields: [
        {
          name: 'issueType',
          label: t('dialogs.reportIssue.issueType'),
          type: 'select',
          required: true,
          defaultValue: '',
          options: issueTypeOptions.map(([value, key]) => ({ value, label: t(`dialogs.reportIssue.issueTypes.${key}`) }))
        },
        {
          name: 'description',
          label: t('dialogs.reportIssue.description'),
          type: 'textarea',
          placeholder: t('operatorPage.placeholders.describeIssue'),
          required: true,
          defaultValue: ''
        },
        {
          name: 'severity',
          label: t('dialogs.reportIssue.severityLevel'),
          type: 'select',
          required: true,
          defaultValue: '',
          options: severityOptions.map(([value, key]) => ({ value, label: t(`dialogs.reportIssue.severityLevels.${key}`) }))
        }
      ],
      onSubmit: async (values) => {
        try {
          const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/report-issue`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              issue_type: values.issueType || 'OTHER',
              severity: values.severity || 'MEDIUM',
              description: values.description
            })
          });
          if (response.ok) {
            showSuccess(t('operatorDashboard.reportIssue.reportedTitle'), t('operatorDashboard.reportIssue.reportedMessage'));
          } else {
            const err = await response.json().catch(() => ({}));
            showError(t('errors.generic.error'), err.detail || err.message || t('operatorPage.errors.reportFailed'));
          }
        } catch (error) {
          console.error('Error reporting issue:', error);
          showError(t('errors.generic.error'), t('operatorPage.errors.reportConnection'));
        }
      }
    });
  };

  const handleViewMaterials = async (taskId) => {
    if (selectedTask?.status === 'COMPLETED') return;
    if (!selectedTask?.production_order_id) {
      showError(t('errors.generic.error'), t('errors.materials.noProductionOrder'));
      return;
    }

    // Show action selector modal
    setShowMaterialActionModal(true);
  };

  const handleRequestMaterials = async () => {
    setShowMaterialActionModal(false);

    // Fetch BOM materials for this production order
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/production-order/${selectedTask.production_order_id}/available-items`));
      if (response.ok) {
        const materials = await response.json();
        if (materials.length === 0) {
          showWarning(t('warnings.noMaterialsAvailable.title'), t('warnings.noMaterialsAvailable.message'));
          return;
        }
        setAvailableMaterials(materials || []);
      }
    } catch (error) {
      console.error('Error fetching BOM materials:', error);
      showError(t('errors.generic.error'), t('errors.materials.loadFailed'));
      return;
    }

    setRequisitionItems([{ inventory_item_id: '', requested_quantity: '', notes: '' }]);
    setShowRequisitionModal(true);
  };

  const handleReturnMaterials = async () => {
    setShowMaterialActionModal(false);

    // Fetch issued materials for this task
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${selectedTask.task_id}/issued-materials`));
      if (response.ok) {
        const materials = await response.json();
        if (materials.length === 0) {
          showWarning(t('warnings.noMaterialsIssued.title'), t('warnings.noMaterialsIssued.message'));
          return;
        }
        setIssuedMaterials(materials || []);
        setReturnItems([{ material_key: '', quantity_returned: '', notes: '' }]);
        setShowReturnModal(true);
      }
    } catch (error) {
      console.error('Error fetching issued materials:', error);
      showError(t('errors.generic.error'), t('errors.materials.issuedLoadFailed'));
    }
  };

  const handleRecordScrap = async () => {
    setShowMaterialActionModal(false);

    // Fetch issued materials for this task
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/task/${selectedTask.task_id}/issued-materials`));
      if (response.ok) {
        const materials = await response.json();
        if (materials.length === 0) {
          showWarning(t('warnings.noMaterialsIssued.title'), t('warnings.noMaterialsIssued.messageScrap'));
          return;
        }
        setIssuedMaterials(materials || []);
        setScrapItems([{ inventory_item_id: '', scrap_quantity: '', scrap_reason: '' }]);
        setShowScrapModal(true);
      }
    } catch (error) {
      console.error('Error fetching issued materials for scrap:', error);
      showError(t('errors.generic.error'), t('errors.materials.issuedLoadFailed'));
    }
  };

  const handleAddRequisitionItem = () => {
    setRequisitionItems([...requisitionItems, { inventory_item_id: '', requested_quantity: '', notes: '' }]);
  };

  const handleRemoveRequisitionItem = (index) => {
    setRequisitionItems(requisitionItems.filter((_, i) => i !== index));
  };

  const validateRequisitionQuantities = async (items) => {
    if (!selectedTask?.production_order_id || !items.some(item => item.inventory_item_id && item.requested_quantity > 0)) {
      setValidationResults([]);
      return;
    }

    try {
      setValidatingQuantities(true);
      const validItems = items.filter(item => item.inventory_item_id && item.requested_quantity > 0);

      const requestBody = {
        production_order_id: selectedTask.production_order_id,
        production_task_id: selectedTask.task_id,
        priority: 'NORMAL',
        notes: 'Validation check',
        items: validItems.map(item => ({
          inventory_item_id: parseInt(item.inventory_item_id),
          requested_quantity: parseFloat(item.requested_quantity),
          notes: item.notes
        }))
      };

      const response = await apiFetch(getApiUrl('/api/inventory-requisitions/validate-quantities'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResults(data.validation_results || []);
      } else {
        setValidationResults([]);
      }
    } catch (error) {
      console.error('Error validating quantities:', error);
      setValidationResults([]);
    } finally {
      setValidatingQuantities(false);
    }
  };

  const handleRequisitionItemChange = (index, field, value) => {
    const updated = [...requisitionItems];
    updated[index][field] = value;
    setRequisitionItems(updated);

    // Trigger validation when quantity or item changes
    if (field === 'requested_quantity' || field === 'inventory_item_id') {
      const debounceTimer = setTimeout(() => {
        validateRequisitionQuantities(updated);
      }, 500); // Debounce validation by 500ms

      // Clear previous timer if user is still typing
      if (handleRequisitionItemChange.timer) {
        clearTimeout(handleRequisitionItemChange.timer);
      }
      handleRequisitionItemChange.timer = debounceTimer;
    }
  };

  const handleAddReturnItem = () => {
    setReturnItems([...returnItems, { material_key: '', quantity_returned: '', reason: '' }]);
  };

  const handleRemoveReturnItem = (index) => {
    setReturnItems(returnItems.filter((_, i) => i !== index));
  };

  const handleReturnItemChange = (index, field, value) => {
    const updated = [...returnItems];
    updated[index][field] = value;
    setReturnItems(updated);
  };

  const handleAddScrapItem = () => {
    setScrapItems([...scrapItems, { inventory_item_id: '', scrap_quantity: '', scrap_reason: '' }]);
  };

  const handleRemoveScrapItem = (index) => {
    setScrapItems(scrapItems.filter((_, i) => i !== index));
  };

  const handleScrapItemChange = (index, field, value) => {
    const updated = [...scrapItems];
    updated[index][field] = value;
    setScrapItems(updated);
  };

  const handleSubmitRequisition = async (bypassValidation = false) => {
    const validItems = requisitionItems.filter(item => item.inventory_item_id && item.requested_quantity > 0);

    if (validItems.length === 0) {
      showWarning(t('warnings.invalidInput.title'), t('warnings.invalidInput.addMaterials'));
      return;
    }

    if (!selectedTask?.production_order_id) {
      showError(t('errors.generic.error'), t('errors.requisition.noProductionOrder'));
      return;
    }

    // Check validation results before submitting (unless bypassed)
    if (!bypassValidation && validationResults.length > 0) {
      const hasErrors = validationResults.some(result => result.status === 'error');
      const hasWarnings = validationResults.some(result => result.status === 'warning');

      if (hasErrors) {
        setShowOverBomDialog(true);
        return;
      }

      if (hasWarnings) {
        const warningMessages = validationResults
          .filter(result => result.status === 'warning')
          .map(result => `${result.item_name}: ${result.message}`)
          .join('\n');

        setConfirmDialog({
          show: true,
          title: t('warnings.bomQuantity.title'),
          message: t('operatorDashboard.bomWarning.message', { items: warningMessages }),
          onConfirm: () => {
            setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
            handleSubmitRequisition(true); // Bypass validation on retry
          }
        });
        return;
      }
    }

    try {
      const bomErrors = validationResults.filter(r => r.status === 'error');
      const requestBody = {
        production_order_id: selectedTask.production_order_id,
        production_task_id: selectedTask.task_id,
        priority: 'NORMAL',
        notes: `Material request from task: ${selectedTask.task_code}`,
        is_over_bom: bomErrors.length > 0,
        over_bom_reason: bomErrors.length > 0 ? (overBomReason || null) : null,
        items: validItems.map(item => ({
          inventory_item_id: parseInt(item.inventory_item_id),
          requested_quantity: parseFloat(item.requested_quantity),
          notes: item.notes || null
        }))
      };

      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/create?requested_by=${user.user_id}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.requisition.created'), t('success.requisition.createdMessage', { requisitionNumber: result.requisition_number }));
        setShowRequisitionModal(false);
        setShowOverBomDialog(false);
        setOverBomReason('');
        setValidationResults([]); // Clear validation results
        setRequisitionItems([{ inventory_item_id: '', requested_quantity: '', notes: '' }]); // Reset form
        // Refresh received materials if on that tab
        if (activeTab === 'materials') {
          fetchReceivedMaterials();
        }
      } else {
        const error = await response.json();
        showError(t('errors.requisition.createFailed'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error creating requisition:', error);
      showError(t('errors.generic.error'), t('errors.requisition.createFailedGeneric'));
    }
  };

  // Issued materials come one row per requisition; a return belongs to one requisition
  const issuedMaterialKey = (mat) => `${mat.requisition_id}:${mat.inventory_item_id}`;

  const handleSubmitReturn = async () => {
    const validItems = returnItems.filter(item => item.material_key && item.quantity_returned > 0);

    if (validItems.length === 0) {
      showWarning(t('warnings.invalidInput.title'), t('warnings.invalidInput.addMaterialsReturn'));
      return;
    }

    if (!selectedTask?.task_id || !selectedTask?.production_order_id) {
      showError(t('errors.generic.error'), t('errors.returns.noTaskInfo'));
      return;
    }

    if (submittingReturnRef.current) return;

    // One return per requisition, like the tablet
    const byRequisition = {};
    validItems.forEach(item => {
      const [requisitionId] = item.material_key.split(':');
      (byRequisition[requisitionId] = byRequisition[requisitionId] || []).push(item);
    });

    const created = [];
    submittingReturnRef.current = true;
    setSubmittingReturn(true);
    try {
      for (const [requisitionId, reqItems] of Object.entries(byRequisition)) {
        const requestBody = {
          requisition_id: parseInt(requisitionId),
          production_task_id: selectedTask.task_id,
          production_order_id: selectedTask.production_order_id,
          return_reason: `Unused materials from task ${selectedTask.task_code}`,
          notes: `Material return from task: ${selectedTask.task_code}`,
          items: reqItems.map(item => ({
            inventory_item_id: parseInt(item.material_key.split(':')[1]),
            quantity_returned: parseFloat(item.quantity_returned),
            notes: item.reason || null
          }))
        };

        const response = await apiFetch(getApiUrl(`/api/material-returns/create?returned_by=${user.user_id}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const error = await response.json();
          // The backend refuses the same return sent twice and names the one already registered
          const message = error.details?.rule === 'material_return_duplicate'
            ? t('errors.returns.duplicate', { returnNumber: error.details.return_number })
            : error.detail || error.message || t('errors.generic.unknownError');
          showError(t('errors.returns.createFailed'), created.length
            ? t('errors.returns.partiallyCreated', { returnNumbers: created.join(', '), message })
            : message);
          return;
        }
        const result = await response.json();
        created.push(result.return_number);
        // Drop what was already sent so a retry does not duplicate it
        setReturnItems(prev => prev.filter(item => !item.material_key.startsWith(`${requisitionId}:`)));
      }
      showSuccess(t('success.returns.created'), t('success.returns.createdMessage', { returnNumber: created.join(', ') }));
      setShowReturnModal(false);
    } catch (error) {
      console.error('Error creating material return:', error);
      showError(t('errors.generic.error'), t('errors.returns.createFailedGeneric'));
    } finally {
      submittingReturnRef.current = false;
      setSubmittingReturn(false);
    }
  };

  const handleSubmitScrap = async () => {
    const validItems = scrapItems.filter(item => item.inventory_item_id && item.scrap_quantity > 0 && item.scrap_reason);

    if (validItems.length === 0) {
      showWarning(t('warnings.invalidInput.title'), t('warnings.invalidInput.addMaterialsScrap'));
      return;
    }

    if (!selectedTask?.task_id || !selectedTask?.production_order_id) {
      showError(t('errors.generic.error'), t('errors.scrap.noTaskInfo'));
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      for (const item of validItems) {
        try {
          const requestBody = {
            item_id: parseInt(item.inventory_item_id),
            production_order_id: selectedTask.production_order_id,
            production_task_id: selectedTask.task_id,
            scrap_quantity: parseFloat(item.scrap_quantity),
            scrap_reason: item.scrap_reason,
            batch_number: null, // Optional
            recorded_by: user.user_id
          };

          const response = await apiFetch(getApiUrl('/api/inventory-issuing/scrap'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (itemError) {
          console.error('Error recording scrap for item:', itemError);
          failCount++;
        }
      }

      if (successCount > 0) {
        showSuccess(t('success.scrap.recorded'), t('success.scrap.recordedMessage', { successCount, failMessage: failCount > 0 ? t('operatorDashboard.scrapFailMessage', { count: failCount }) : '' }));
        setShowScrapModal(false);
        setScrapItems([]);
      } else {
        showError(t('errors.scrap.recordFailed'), t('errors.scrap.recordFailedMessage'));
      }
    } catch (error) {
      console.error('Error recording scrap:', error);
      showError(t('errors.generic.error'), t('errors.scrap.recordFailedGeneric'));
    }
  };

  const fetchReceivedMaterials = async () => {
    try {
      setLoadingMaterials(true);
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/my-requisitions/${user.user_id}?limit=100`));
      if (response.ok) {
        const data = await response.json();
        // Show all requisitions so operators can see the full workflow
        setReceivedMaterials(data);
      }
    } catch (error) {
      console.error('Error fetching received materials:', error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleViewReceipt = async (requisition) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/${requisition.requisition_id}`));
      if (response.ok) {
        const data = await response.json();
        setSelectedReceipt(data);
        setShowReceiptModal(true);
      }
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      showError(t('errors.generic.error'), t('errors.receipt.loadDetailsFailed'));
    }
  };

  const fetchWorkflowProgress = async (workflowInstanceId) => {
    if (!workflowInstanceId) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/workflow-progress/${workflowInstanceId}`));
      if (response.ok) {
        const progress = await response.json();
        setWorkflowProgress(progress);
      } else {
        console.error('Failed to fetch workflow progress:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch workflow progress:', error);
    }
  };

  const handleTaskClick = async (task) => {
    let resolvedTask = task;

    // If task has no production_order_id but has a parent (split task), resolve from parent
    if (!task.production_order_id && task.parent_task_id) {
      try {
        const parentResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.parent_task_id}`));
        if (parentResponse.ok) {
          const parentTask = await parentResponse.json();
          if (parentTask.production_order_id) {
            resolvedTask = { ...task, production_order_id: parentTask.production_order_id };
          }
        }
      } catch (err) {
        console.warn('Could not resolve production_order_id from parent task:', err);
      }
    }

    setSelectedTask(resolvedTask);

    // Fetch time logs for this task
    try {

      // Get time logs
      const logsResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/time-logs`));
      if (logsResponse.ok) {
        const logs = await logsResponse.json();
        setTaskTimeLogs(logs);
      }

      // Get time summary
      const summaryResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/time-summary`));
      if (summaryResponse.ok) {
        const summary = await summaryResponse.json();
        setTaskTimeSummary(summary);
      }
    } catch (error) {
      console.error('Error fetching time data:', error);
    }

    if (task.workflow_instance_id) {
      await fetchWorkflowProgress(task.workflow_instance_id);
    } else {
      setWorkflowProgress([]);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT': return '#ff6b6b';
      case 'HIGH': return '#ffa94d';
      case 'NORMAL': return '#a9d4ff';
      case 'LOW': return '#c8c8c8';
      default: return '#c8c8c8';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'IN_PROGRESS': return { bg: '#1565C0', border: '#2196F3', pulse: true };
      case 'PAUSED':      return { bg: '#E65100', border: '#FF9800', pulse: false };
      case 'ASSIGNED':    return { bg: '#4A148C', border: '#9C27B0', pulse: false };
      case 'COMPLETED':   return { bg: '#1B5E20', border: '#4CAF50', pulse: false };
      default:            return { bg: '#1a1a2e', border: '#444',    pulse: false };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'IN_PROGRESS': return `▶ ${t('taskStatus.inProgress')}`;
      case 'PAUSED':      return `⏸ ${t('taskStatus.paused')}`;
      case 'ASSIGNED':    return `● ${t('taskStatus.assigned')}`;
      case 'COMPLETED':   return `✓ ${t('taskStatus.completed')}`;
      default:            return status;
    }
  };

  const getNextActionLabel = (task) => {
    if (task.status === 'ASSIGNED')    return t('actions.nextStartTask');
    if (task.status === 'IN_PROGRESS') return t('actions.activePauseOrComplete');
    if (task.status === 'PAUSED')      return t('actions.nextResumeTask');
    return null;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return t('time.notAvailable');
    if (minutes < 60) return `${minutes}${t('time.minutes')}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}${t('time.hours')} ${mins}${t('time.minutes')}`;
  };

  // Format time from seconds for timer display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    if (!activeTask?.estimated_duration_minutes) return 0;
    const estimatedSeconds = activeTask.estimated_duration_minutes * 60;
    const percentage = (elapsedTime / estimatedSeconds) * 100;
    return Math.min(percentage, 200); // Cap at 200% for visualization
  };

  // Start timer and create time log
  const handleStartTimer = async (task) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/time-logs/start?task_id=${task.task_id}&activity_type=WORK`), {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to start time log');
      }

      const data = await response.json();
      setActiveTimeLogId(data.log_id);
      setElapsedTime(0);
      setTimerRunning(true);

      // Update task status to IN_PROGRESS
      await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/status?status=IN_PROGRESS`), {
        method: 'PATCH'
      });

      await fetchOperatorDashboard();
    } catch (error) {
      console.error('Error starting timer:', error);
    }
  };

  // Stop timer and end time log
  const handleStopTimer = async () => {
    try {

      if (activeTimeLogId) {
        await apiFetch(getApiUrl(`/api/production-tasks/time-logs/${activeTimeLogId}/end`), {
          method: 'PATCH'
        });
      }

      setTimerRunning(false);
      setActiveTimeLogId(null);

      // Update actual duration on the task
      if (activeTask) {
        await apiFetch(getApiUrl(`/api/production-tasks/tasks/${activeTask.task_id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            actual_duration_minutes: Math.floor(elapsedTime / 60)
          })
        });
      }

      await fetchOperatorDashboard();
    } catch (error) {
      console.error('Error stopping timer:', error);
    }
  };

  // Pause/Resume timer
  const handlePauseResume = () => {
    setTimerRunning(!timerRunning);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'PENDING': 'status-pending',
      'IN_PROGRESS': 'status-in-progress',
      'COMPLETED': 'status-success',
      'CANCELLED': 'status-cancelled',
      'ASSIGNED': 'status-assigned'
    };
    return statusMap[status] || '';
  };

  // Task type-specific rendering function
  const renderTaskByType = (task) => {
    if (!task || !task.task_type) {
      return null;
    }

    const commonProps = {
      task: task,
      onTaskAction: (action, data) => {
        // Handle task actions like start, pause, complete
        switch (action) {
          case 'start':
            handleStartTask(task.task_id);
            break;
          case 'pause':
            handlePauseTask(task.task_id);
            break;
          case 'resume':
            handleResumeTask(task.task_id);
            break;
          case 'complete':
            handleCompleteTask(task.task_id);
            break;
          default:
            console.log('Unknown task action:', action, data);
        }
      },
      onComplete: (completionData) => {
        // Handle task completion with type-specific data
        console.log('Task completed with data:', completionData);
        handleCompleteTask(task.task_id);

        // Could send type-specific completion data to backend here
        // await apiFetch(getApiUrl(`/api/tasks/${task.task_id}/complete`), {
        //   method: 'POST',
        //   body: JSON.stringify(completionData)
        // });
      }
    };

    // Render different components based on special_form from task type config
    const specialForm = taskTypeMap[task.task_type]?.special_form || null;
    switch (specialForm) {
      case 'SETUP':
        return <SetupTaskInterface key={task.task_id} {...commonProps} />;
      case 'PRINTING':
        return <PrintingTaskInterface key={task.task_id} {...commonProps} />;
      case 'DIE_CUTTING':
        return <CuttingTaskInterface key={task.task_id} {...commonProps} />;
      case 'SEAMED':
        return <SeamedTaskInterface key={task.task_id} {...commonProps} />;
      case 'QUALITY_CHECK':
        return <QualityCheckTaskInterface key={task.task_id} {...commonProps} />;
      case 'MATERIAL_PREP':
        return <MaterialPrepTaskInterface key={task.task_id} {...commonProps} />;
      default:
        // Return null to show the default task interface
        return null;
    }
  };

  if (loading) {
    return (
      <div className="operator-dashboard">
        <div className="loading">{t('loading.dashboard')}</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .operator-dashboard .blue-task-panel {
          background: #1976D2 !important;
          background-color: #1976D2 !important;
          background-image: linear-gradient(135deg, #1976D2 0%, #1565C0 100%) !important;
          border-radius: 12px !important;
          padding: 24px !important;
          margin-bottom: 24px !important;
          box-shadow: 0 10px 30px rgba(25, 118, 210, 0.4) !important;
          color: white !important;
          width: 100% !important;
          max-height: calc(100vh - 250px) !important;
          overflow-y: auto !important;
        }
        .operator-dashboard .blue-task-panel div,
        .operator-dashboard .blue-task-panel h2,
        .operator-dashboard .blue-task-panel h3,
        .operator-dashboard .blue-task-panel h4,
        .operator-dashboard .blue-task-panel h5,
        .operator-dashboard .blue-task-panel p,
        .operator-dashboard .blue-task-panel span,
        .operator-dashboard .blue-task-panel label,
        .operator-dashboard .blue-task-panel td,
        .operator-dashboard .blue-task-panel th {
          color: white !important;
          background: transparent !important;
          background-color: transparent !important;
        }
        .operator-dashboard .blue-task-panel h4 {
          opacity: 0.9 !important;
          font-weight: 500 !important;
        }
        /* Form controls: keep explicit dark background so native dropdowns are readable */
        .operator-dashboard .blue-task-panel select,
        .operator-dashboard .blue-task-panel input[type="text"],
        .operator-dashboard .blue-task-panel input[type="number"],
        .operator-dashboard .blue-task-panel textarea {
          background: rgba(0, 0, 0, 0.35) !important;
          background-color: rgba(0, 0, 0, 0.35) !important;
          color: white !important;
          border: 1px solid rgba(255, 255, 255, 0.35) !important;
          border-radius: 4px !important;
        }
        .operator-dashboard .blue-task-panel select option {
          background: #1a237e !important;
          background-color: #1a237e !important;
          color: white !important;
        }
        /* Custom scrollbar for task list */
        .operator-dashboard .task-list::-webkit-scrollbar {
          width: 8px;
        }
        .operator-dashboard .task-list::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .operator-dashboard .task-list::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        .operator-dashboard .task-list::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    <div className="operator-dashboard">
      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          📋 {t('tabs.tasks')}
        </button>
        <button
          className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          📦 {t('tabs.materials')}
        </button>
      </div>

      {/* Tasks Tab Content */}
      {activeTab === 'tasks' && (
        <div className="tab-content" style={{
          display: 'flex',
          flexDirection: selectedTask ? 'row' : 'column',
          gap: '2rem'
        }}>
          {/* Selected Task Detail Panel */}
          {selectedTask && (() => {
            const panelColors = getStatusColor(selectedTask.status);
            return (
            <div
              className="blue-task-panel"
              style={{
                background: `linear-gradient(135deg, ${panelColors.bg} 0%, ${panelColors.bg}cc 100%)`,
                borderRadius: '12px',
                padding: '24px',
                boxShadow: `0 10px 30px ${panelColors.border}44`,
                color: 'white',
                flex: '0 0 50%',
                maxWidth: '50%',
                maxHeight: 'calc(100vh - 250px)',
                overflowY: 'auto'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
                background: 'transparent'
              }}>
                <div>
                  <h2 style={{ fontSize: '24px', margin: '0 0 8px 0', color: 'white', fontWeight: '600' }}>
                    {selectedTask.task_name}
                  </h2>
                  {selectedTask.po_number && (
                    <div style={{
                      fontSize: '15px',
                      color: '#60a5fa',
                      marginBottom: '8px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📦 {selectedTask.po_number}</span>
                      {selectedTask.customer_name && (
                        <>
                          <span style={{ opacity: 0.5 }}>•</span>
                          <span>{selectedTask.customer_name}</span>
                        </>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '14px', color: 'white', opacity: '0.95' }}>
                    <span>{t('operatorDashboard.detail.code', { code: selectedTask.task_code })}</span>
                    <span>•</span>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: selectedTask.priority === 'HIGH' ? '#FF5722' :
                        selectedTask.priority === 'MEDIUM' ? '#FFC107' : '#8BC34A',
                      color: 'white'
                    }}>
                      {selectedTask.priority}
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background:
                        selectedTask.status === 'IN_PROGRESS' ? '#2196F3' :
                        selectedTask.status === 'ASSIGNED' ? '#9C27B0' :
                        selectedTask.status === 'PAUSED' ? '#FF9800' :
                        selectedTask.status === 'COMPLETED' ? '#4CAF50' :
                        'rgba(255, 255, 255, 0.2)',
                      color: 'white'
                    }}>
                      {selectedTask.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Banner */}
              <div style={{
                background: panelColors.border + '33',
                border: `1px solid ${panelColors.border}`,
                borderRadius: '8px',
                padding: '8px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '15px',
                fontWeight: '600'
              }}>
                {getStatusLabel(selectedTask.status)}
                {getNextActionLabel(selectedTask) && (
                  <span style={{ fontSize: '13px', opacity: 0.8, marginLeft: 'auto' }}>
                    {getNextActionLabel(selectedTask)}
                  </span>
                )}
              </div>

              {/* Workflow Progress */}
              {workflowProgress && workflowProgress.length > 0 ? (
                <div className="workflow-breadcrumb" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 40px',
                  marginBottom: '20px',
                  background: 'transparent',
                  position: 'relative',
                  overflowX: 'auto',
                  minHeight: '120px'
                }}>

                  {/* Workflow steps */}
                  {workflowProgress.map((step, index, arr) => {
                    const isCurrent = step.task_id === selectedTask.task_id;
                    const isCompleted = step.status === 'COMPLETED';
                    const isInProgress = step.status === 'IN_PROGRESS';
                    const currentIndex = workflowProgress.findIndex(s => s.task_id === selectedTask.task_id);
                    const isPast = index < currentIndex;
                    const isLast = index === arr.length - 1;

                    return (
                      <React.Fragment key={step.task_id}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                          zIndex: 10,
                          flex: '0 0 auto',
                          minWidth: '120px'
                        }}>
                          {/* Circle */}
                          <div style={{
                            width: isCurrent ? '52px' : '44px',
                            height: isCurrent ? '52px' : '44px',
                            borderRadius: '50%',
                            background: isCompleted ? 'linear-gradient(135deg, #4CAF50, #45a049)' :
                                       isCurrent ? 'linear-gradient(135deg, #2196F3, #1976D2)' :
                                       isInProgress ? '#FFA726' :
                                       'linear-gradient(135deg, #757575, #616161)',
                            border: isCurrent ? '4px solid #FFD700' :
                                   isCompleted ? '3px solid #81C784' :
                                   isInProgress ? '3px solid #FFB74D' :
                                   '2px solid rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: isCurrent ? '20px' : '16px',
                            fontWeight: 'bold',
                            boxShadow: isCurrent ? '0 0 30px rgba(255, 215, 0, 0.6), 0 4px 12px rgba(0,0,0,0.3)' :
                                      isCompleted ? '0 0 20px rgba(76, 175, 80, 0.4), 0 4px 8px rgba(0,0,0,0.2)' :
                                      isInProgress ? '0 0 15px rgba(255, 167, 38, 0.4)' :
                                      '0 2px 4px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            zIndex: 10,
                            transform: isCurrent ? 'scale(1.1)' : 'scale(1)'
                          }}>
                            {isCompleted ? '✓' : isInProgress ? '⚡' : index + 1}
                          </div>

                          {/* Status Badge */}
                          {(isCompleted || isInProgress) && (
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '50%',
                              transform: 'translateX(50%)',
                              background: isCompleted ? '#4CAF50' : '#FFA726',
                              color: 'white',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}>
                              {isCompleted ? t('taskProgress.done') : t('taskProgress.inProgress')}
                            </div>
                          )}

                          {/* Task name */}
                          <div style={{
                            marginTop: '12px',
                            textAlign: 'center',
                            fontSize: '12px',
                            color: isCompleted ? '#A5D6A7' :
                                   isCurrent ? '#FFD700' :
                                   isInProgress ? '#FFB74D' :
                                   'rgba(255, 255, 255, 0.5)',
                            fontWeight: isCurrent ? '700' : isCompleted ? '500' : '400',
                            background: 'transparent',
                            maxWidth: '100px',
                            textShadow: isCurrent ? '0 0 10px rgba(255, 215, 0, 0.5)' : 'none'
                          }}>
                            {step.workflow_task_name || step.task_name}
                          </div>

                          {/* Time if current */}
                          {isCurrent && step.estimated_duration_minutes && (
                            <div style={{
                              fontSize: '10px',
                              color: 'rgba(255, 255, 255, 0.7)',
                              background: 'transparent',
                              marginTop: '4px'
                            }}>
                              {t('operatorDashboard.detail.estimate', { duration: formatDuration(step.estimated_duration_minutes) })}
                            </div>
                          )}
                        </div>

                        {/* Connecting line to next step */}
                        {!isLast && (
                          <div style={{
                            flex: '1 1 auto',
                            minWidth: '40px',
                            maxWidth: '120px',
                            height: '0',
                            borderTop: isCompleted ? '3px solid #4CAF50' : '3px solid rgba(255, 255, 255, 0.3)',
                            alignSelf: 'flex-start',
                            marginTop: '26px',
                            position: 'relative',
                            zIndex: 1,
                            boxShadow: isCompleted ? '0 0 8px rgba(76, 175, 80, 0.3)' : 'none'
                          }}></div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                selectedTask?.workflow_instance_id && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    color: 'white',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    {t('operatorDashboard.detail.loadingWorkflow', { id: selectedTask.workflow_instance_id })}
                  </div>
                )
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '24px',
                background: 'transparent'
              }}>
                <div>
                  <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('common:ui.product')}</h4>
                  <p style={{ margin: '0', fontSize: '16px', color: 'white' }}>
                    {selectedTask.product_name || t('time.notAvailable')}
                  </p>
                </div>
                <div>
                  <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('operatorDashboard.detail.scheduledStart')}</h4>
                  <p style={{ margin: '0', fontSize: '16px', color: 'white' }}>
                    {selectedTask.scheduled_start
                      ? formatAppDate(selectedTask.scheduled_start, 'datetime')
                      : t('taskProgress.notScheduled')}
                  </p>
                </div>
                {selectedTask.workflow_name && (
                  <div>
                    <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('operatorDashboard.detail.workflow')}</h4>
                    <p style={{ margin: '0', fontSize: '16px', color: 'white' }}>
                      {selectedTask.workflow_name}
                    </p>
                  </div>
                )}
                {selectedTask.workflow_task_name && (
                  <div>
                    <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('operatorDashboard.detail.workflowStep')}</h4>
                    <p style={{ margin: '0', fontSize: '16px', color: 'white' }}>
                      {selectedTask.workflow_task_name}
                    </p>
                  </div>
                )}
              </div>

              {selectedTask.description && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('common:ui.description')}</h4>
                  <p style={{ margin: '0', fontSize: '14px', lineHeight: '1.5', color: 'white' }}>
                    {selectedTask.description}
                  </p>
                </div>
              )}

              {/* Type-specific Task Interface */}
              {selectedTask.task_type && taskTypeMap[selectedTask.task_type]?.special_form && (
                <div style={{ marginBottom: '24px' }}>
                  {renderTaskByType(selectedTask)}
                </div>
              )}

              {/* Materials Summary Panel */}
              {selectedTask.production_order_id && (
                <div style={{ marginBottom: '24px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', overflow: 'hidden' }}>
                  <button
                    onClick={() => {
                      const willExpand = !taskMaterialsExpanded;
                      setTaskMaterialsExpanded(willExpand);
                      if (willExpand && !taskMaterials[selectedTask.task_id]) {
                        fetchTaskMaterials(selectedTask.task_id);
                      }
                    }}
                    style={{
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontWeight: '600',
                    }}
                  >
                    <span>{t('operatorDashboard.materialsPanel.title')}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {taskMaterialsLoading && <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('common:ui.loading')}</span>}
                      {taskMaterialsExpanded
                        ? <button
                            onClick={(e) => { e.stopPropagation(); refreshTaskMaterials(selectedTask.task_id); }}
                            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '4px', color: 'white', padding: '2px 8px', fontSize: '11px', cursor: 'pointer' }}
                          >↻ {t('common:ui.refresh')}</button>
                        : null
                      }
                      <span>{taskMaterialsExpanded ? '▲' : '▼'}</span>
                    </span>
                  </button>

                  {taskMaterialsExpanded && (
                    <div style={{ padding: '0 16px 16px' }}>
                      {taskMaterialsLoading && !taskMaterials[selectedTask.task_id] ? (
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{t('operatorDashboard.loadingMaterials')}</p>
                      ) : !taskMaterials[selectedTask.task_id] || taskMaterials[selectedTask.task_id].length === 0 ? (
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontStyle: 'italic' }}>
                          {t('operatorDashboard.materialsPanel.noBom')}
                        </p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr>
                              {[
                                t('common:ui.material'),
                                t('operatorDashboard.materialsPanel.columns.bom'),
                                t('operatorDashboard.materialsPanel.columns.requested'),
                                t('operatorDashboard.materialsPanel.columns.issued'),
                                t('common:ui.status'),
                              ].map((h, i) => (
                                <th key={i} style={{
                                  padding: '6px 8px',
                                  textAlign: i === 0 ? 'left' : 'center',
                                  color: 'rgba(255,255,255,0.6)',
                                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                                  fontSize: '11px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {taskMaterials[selectedTask.task_id].map(mat => (
                              <tr key={mat.inventory_item_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <td style={{ padding: '8px 8px', color: 'white' }}>
                                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', display: 'block' }}>{mat.item_code}</span>
                                  {mat.item_name}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
                                  {mat.bom_quantity > 0 ? `${mat.bom_quantity} ${mat.unit_of_measure}` : '—'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
                                  {mat.total_requested > 0 ? `${mat.total_requested} ${mat.unit_of_measure}` : '—'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
                                  {mat.total_issued > 0 ? `${mat.total_issued} ${mat.unit_of_measure}` : '—'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <span style={{
                                    ...getMaterialStatusStyle(mat.status),
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                  }}>
                                    {mat.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Time Tracking Section */}
              {taskTimeSummary && taskTimeSummary.total_minutes > 0 && (
                <div style={{ marginBottom: '24px', background: 'rgba(255, 255, 255, 0.05)', padding: '16px', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '12px', color: 'white', opacity: '0.8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {t('operatorDashboard.timeTracking.title')}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: '0', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{t('operatorDashboard.timeTracking.totalTime')}</p>
                      <p style={{ margin: '0', fontSize: '18px', color: 'white', fontWeight: 'bold' }}>
                        {Math.floor(taskTimeSummary.total_minutes / 60)}h {taskTimeSummary.total_minutes % 60}m
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{t('operatorDashboard.timeTracking.sessions')}</p>
                      <p style={{ margin: '0', fontSize: '18px', color: 'white', fontWeight: 'bold' }}>
                        {taskTimeSummary.log_count}
                      </p>
                    </div>
                  </div>

                  {/* Time Log History */}
                  {taskTimeLogs.length > 0 && (
                    <div>
                      <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', textTransform: 'uppercase' }}>{t('operatorDashboard.timeTracking.recentSessions')}</p>
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {taskTimeLogs.slice(0, 5).map((log, index) => (
                          <div key={log.log_id} style={{
                            padding: '8px',
                            background: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                            borderRadius: '4px',
                            marginBottom: '4px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                              <span style={{ color: 'white' }}>{log.user_name || log.username}</span>
                              <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {log.duration_minutes ? `${log.duration_minutes}m` : t('common:ui.inProgress')}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                              <span>{formatAppDate(log.start_time)}</span>
                              <span>{formatAppDate(log.start_time, 'time')} - {log.end_time ? formatAppDate(log.end_time, 'time') : t('operatorDashboard.timeTracking.ongoing')}</span>
                            </div>
                            {log.notes && (
                              <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginTop: '4px', margin: '4px 0 0 0' }}>
                                {log.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="detail-panel-actions" style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {selectedTask.status === 'ASSIGNED' && selectedTask.assigned_to === user?.user_id && (
                  <button
                    className="task-action-button start-btn"
                    onClick={() => handleStartTask(selectedTask.task_id)}
                    style={{
                      background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #1976D2, #1565C0)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #2196F3, #1976D2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {t('operatorDashboard.actions.startTask')}
                  </button>
                )}
                {selectedTask.status === 'IN_PROGRESS' && selectedTask.assigned_to === user?.user_id && (
                  <button
                    onClick={() => handlePauseTask(selectedTask.task_id)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    ⏸ {t('buttons.pause')}
                  </button>
                )}
                {selectedTask.status === 'IN_PROGRESS' && selectedTask.assigned_to === user?.user_id && (
                  <button
                    onClick={() => setShowShiftHandoffModal(true)}
                    style={{
                      background: 'linear-gradient(135deg, #E65100, #BF360C)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #BF360C, #8D1C00)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #E65100, #BF360C)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    🔚 {t('finalizarTurno.title')}
                  </button>
                )}
                {selectedTask.status === 'PAUSED' && selectedTask.assigned_to === user?.user_id && (
                  <button
                    onClick={() => handleResumeTask(selectedTask.task_id)}
                    style={{
                      background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #1976D2, #1565C0)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #2196F3, #1976D2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    ▶ {t('buttons.resume')}
                  </button>
                )}
                {(selectedTask.status === 'IN_PROGRESS' || selectedTask.status === 'PAUSED') && selectedTask.assigned_to === user?.user_id && (
                  <button
                    className="task-action-button complete-btn"
                    onClick={() => handleCompleteTask(selectedTask.task_id)}
                    style={{
                      background: '#4CAF50',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#66BB6A';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#4CAF50';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    ✓ {t('buttons.complete')}
                  </button>
                )}
                <button
                  className="task-action-button issue-btn"
                  onClick={() => handleReportIssue(selectedTask.task_id)}
                  style={{
                      background: '#C62828',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '14px 28px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#EF5350';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#C62828';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                  {t('operatorDashboard.actions.issue')}
                </button>
                {selectedTask.status !== 'COMPLETED' && selectedTask.assigned_to === user?.user_id && (
                <button
                  className="task-action-button materials-btn"
                  onClick={() => handleViewMaterials(selectedTask.task_id)}
                  style={{
                    background: '#CD7832',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#D4924A';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#CD7832';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  📦 {t('labels.materials')}
                </button>
                )}
              </div>
            </div>
            );
          })()}


          {/* Filtered Task List */}
          <div className="dashboard-section" style={{
            flex: selectedTask ? '1' : 'none',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div className="section-header">
              <h2 className="section-title">
                {taskFilter === 'my-tasks' && t('taskFilters.myAssignedTasks')}
                {taskFilter === 'available' && t('taskFilters.availableTasks')}
                {taskFilter === 'completed-today' && t('taskFilters.completedToday')}
              </h2>
            </div>

            {/* At-a-Glance Summary Strip */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {[
                { id: 'urgent',     label: t('operatorDashboard.chips.urgent'),    count: myTasks.filter(task => task.priority === 'URGENT').length, color: '#e74c3c' },
                { id: 'inProgress', label: t('common:ui.inProgress'),              count: myTasks.filter(task => task.status === 'IN_PROGRESS').length, color: '#2196F3' },
                { id: 'assigned',   label: t('taskStatus.assigned'),               count: myTasks.filter(task => task.status === 'ASSIGNED').length, color: '#9C27B0' },
                { id: 'doneToday',  label: t('operatorDashboard.chips.doneToday'), count: completedToday.length, color: '#4CAF50' },
              ].map(chip => (
                <div key={chip.id} style={{
                  background: chip.color + '22',
                  border: `1px solid ${chip.color}`,
                  borderRadius: '20px',
                  padding: '4px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: chip.color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '18px', fontWeight: '700' }}>{chip.count}</span>
                  {chip.label}
                </div>
              ))}
            </div>

            {/* Task Filter Bar */}
            <div className="filter-bar" style={{ marginTop: '12px', marginBottom: '16px' }}>
              <div className="filter-buttons">
                <button
                  className={`filter-button ${taskFilter === 'my-tasks' ? 'active' : ''}`}
                  onClick={() => setTaskFilter('my-tasks')}
                >
                  {t('taskFilters.myTasks')} ({myTasks.length})
                </button>
                <button
                  className={`filter-button ${taskFilter === 'available' ? 'active' : ''}`}
                  onClick={() => setTaskFilter('available')}
                >
                  {t('taskFilters.available')} ({availableTasks.length})
                </button>
                <button
                  className={`filter-button ${taskFilter === 'completed-today' ? 'active' : ''}`}
                  onClick={() => setTaskFilter('completed-today')}
                >
                  {t('taskFilters.completedTodayShort')} ({completedToday.length})
                </button>
              </div>
            </div>
            <div className="task-list" style={{
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem',
              maxHeight: 'calc(100vh - 350px)'
            }}>
              {filteredTasks.length === 0 ? (
                <div className="empty-state">
                  {taskFilter === 'my-tasks' && t('messages.noAssignedTasks')}
                  {taskFilter === 'available' && t('messages.noAvailableTasks')}
                  {taskFilter === 'completed-today' && t('messages.noTasksCompletedToday')}
                </div>
              ) : (
                filteredTasks.map(task => {
                  const isUrgent = task.priority === 'URGENT';
                  const statusColors = getStatusColor(task.status);
                  const borderColor = isUrgent ? '#e74c3c' : statusColors.border;
                  const bgTint = isUrgent ? 'rgba(231, 76, 60, 0.08)' : `${statusColors.bg}33`;
                  const isSelected = selectedTask?.task_id === task.task_id;
                  return (
                    <div
                      key={task.task_id}
                      className={task.status === 'IN_PROGRESS' ? 'task-card-in-progress' : ''}
                      onClick={() => handleTaskClick(task)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? bgTint + 'aa' : bgTint,
                        border: `1px solid ${borderColor}55`,
                        borderLeft: `3px solid ${borderColor}`,
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        transition: 'all 0.2s',
                        color: '#ffffff'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = bgTint + 'cc';
                          e.currentTarget.style.borderColor = `${borderColor}88`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = bgTint;
                          e.currentTarget.style.borderColor = `${borderColor}55`;
                        }
                      }}
                    >
                      <div className="task-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="task-code">{task.task_code}</span>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#ffffff',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            background: borderColor
                          }}>
                            {getStatusLabel(task.status)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {/* Task Type Indicator */}
                          {task.task_type && (() => {
                            const ttConfig = getTaskTypeConfig(task.task_type);
                            return (
                              <span
                                style={{
                                  background: ttConfig.color,
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {ttConfig.icon}
                                {ttConfig.name}
                              </span>
                            );
                          })()}
                          <span
                            className="task-priority"
                            style={{ color: getPriorityColor(task.priority) }}
                          >
                            {task.priority}
                          </span>
                        </div>
                      </div>
                      <div className="task-name">{task.task_name}</div>
                      {task.po_number && (
                        <div style={{
                          fontSize: '13px',
                          color: '#60a5fa',
                          marginTop: '4px',
                          marginBottom: '4px',
                          fontWeight: '500'
                        }}>
                          📦 {task.po_number}
                          {task.customer_name && ` • ${task.customer_name}`}
                        </div>
                      )}
                      {task.product_name && (
                        <div className="task-product">
                          {task.product_name}
                        </div>
                      )}
                      <div className="task-meta">
                        <span className="task-duration">
                          {taskFilter === 'completed-today'
                            ? t('operatorDashboard.taskCard.completedIn', { duration: formatDuration(task.actual_duration_minutes) })
                            : t('operatorDashboard.taskCard.estimate', { duration: formatDuration(task.estimated_duration_minutes) })
                          }
                        </span>
                        {task.scheduled_start && taskFilter !== 'completed-today' && (
                          <span className="task-schedule">
                            {t('operatorDashboard.taskCard.start', { time: formatAppDate(task.scheduled_start, 'time') })}
                          </span>
                        )}
                      </div>
                      {getNextActionLabel(task) && (
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)', marginTop: '6px', fontWeight: '500' }}>
                          {getNextActionLabel(task)}
                        </div>
                      )}
                      <div className="task-actions" onClick={(e) => e.stopPropagation()}>
                        {task.status === 'ASSIGNED' && (
                          <button className="btn-primary btn-sm" onClick={() => handleStartTask(task.task_id)}>
                            ▶ {t('buttons.start')}
                          </button>
                        )}
                        {task.status === 'PAUSED' && (
                          <button className="btn-primary btn-sm" onClick={() => handleStartTask(task.task_id)}>
                            ▶ {t('buttons.resume')}
                          </button>
                        )}
                        {taskFilter === 'available' && (
                          <button className="btn-accept" onClick={() => handleAcceptTask(task.task_id)}>
                            {t('operatorDashboard.actions.accept')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      )}

      {/* Materials Tab Content */}
      {activeTab === 'materials' && (
        <div className="tab-content">
          <div className="section-header" style={{ marginBottom: '1.5rem' }}>
            <h2>📦 {t('tabs.materials')}</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              {t('operatorDashboard.materialsTab.subtitle')}
            </p>
          </div>

          {loadingMaterials ? (
            <div className="loading">{t('operatorDashboard.loadingMaterials')}</div>
          ) : receivedMaterials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📦</div>
              <p>{t('operatorDashboard.materialsTab.empty')}</p>
              <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                {t('operatorDashboard.materialsTab.emptyHint')}
              </p>
            </div>
          ) : (
            <div className="materials-list">
              {receivedMaterials.map(receipt => (
                <div
                  key={receipt.requisition_id}
                  className="material-receipt-card"
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => handleViewReceipt(receipt)}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                        {receipt.requisition_number}
                      </h3>
                      {receipt.po_number && (
                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                          {t('operatorDashboard.materialsTab.po', { number: receipt.po_number })}
                        </p>
                      )}
                    </div>
                    <span className={`status-badge ${getRequisitionStatusClass(receipt.status)}`}>
                      {getRequisitionStatusText(receipt.status)}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                    <div>
                      <strong>{t('operatorDashboard.materialsTab.requested')}</strong> {formatAppDate(receipt.requested_date, 'datetime')}
                    </div>
                    <div>
                      <strong>{getStatusDateLabel(receipt.status)}:</strong> {receipt.approved_date ? formatAppDate(receipt.approved_date, 'datetime') : t('common:ui.pending')}
                    </div>
                  </div>

                  <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'var(--bg-primary)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                      {t('operatorDashboard.materialsTab.clickToView')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Material Receipt Details Modal */}
      {showReceiptModal && selectedReceipt && (
        <div className="modal-overlay" onClick={() => setShowReceiptModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.receiptModal.title', { number: selectedReceipt.requisition_number })}</h2>
              <button className="modal-close" onClick={() => setShowReceiptModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {/* Receipt Header */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>{t('labels.productionOrder')}:</strong> {selectedReceipt.po_number || t('time.notAvailable')}
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>{t('operatorDashboard.materialsTab.requested')}</strong> {formatAppDate(selectedReceipt.requested_date, 'datetime')}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>{t('common:ui.status')}:</strong> <span className={`status-badge ${getRequisitionStatusClass(selectedReceipt.status)}`}>{getRequisitionStatusText(selectedReceipt.status)}</span>
                    </p>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                      <strong>{getStatusDateLabel(selectedReceipt.status)}:</strong> {selectedReceipt.approved_date ? formatAppDate(selectedReceipt.approved_date, 'datetime') : t('common:ui.pending')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Materials Table */}
              <h3 style={{ marginBottom: '1rem' }}>{t('operatorDashboard.receiptModal.requestedMaterials')}</h3>
              <table className="warehouse-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>{t('operatorDashboard.receiptModal.materialCode')}</th>
                    <th>{t('operatorDashboard.receiptModal.materialName')}</th>
                    <th>{t('common:ui.quantity')}</th>
                    <th>{t('common:ui.unit')}</th>
                    <th>{t('common:ui.notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReceipt.items && selectedReceipt.items.length > 0 ? (
                    selectedReceipt.items.map((item, index) => (
                      <tr key={index}>
                        <td><strong>{item.item_code}</strong></td>
                        <td>{item.item_name}</td>
                        <td><strong>{parseFloat(item.approved_quantity || item.requested_quantity).toLocaleString()}</strong></td>
                        <td>{item.unit_of_measure}</td>
                        <td>{item.notes || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {t('operatorDashboard.receiptModal.noItems')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {selectedReceipt.notes && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <strong>{t('common:ui.notes')}:</strong>
                  <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>{selectedReceipt.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReceiptModal(false)}>
                {t('common:ui.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Action Selector Modal */}
      {showMaterialActionModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowMaterialActionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.materialAction.title')}</h2>
              <button className="modal-close" onClick={() => setShowMaterialActionModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>{t('dialogs.completeTask.infoTask')}:</strong> {selectedTask.task_code} - {selectedTask.task_name}</p>
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {t('operatorDashboard.materialAction.choose')}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleRequestMaterials}
                  style={{
                    padding: '1.5rem',
                    fontSize: '1.1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <span>{t('operatorDashboard.materialAction.request')}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {t('operatorDashboard.materialAction.requestHint')}
                  </span>
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={handleReturnMaterials}
                  style={{
                    padding: '1.5rem',
                    fontSize: '1.1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <span>{t('operatorDashboard.materialAction.return')}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {t('operatorDashboard.materialAction.returnHint')}
                  </span>
                </button>

                <button
                  className="btn btn-warning"
                  onClick={handleRecordScrap}
                  style={{
                    padding: '1.5rem',
                    fontSize: '1.1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    background: 'linear-gradient(135deg, #EF5350, #C62828)',
                    border: 'none',
                    color: 'white'
                  }}
                >
                  <span>{t('operatorDashboard.materialAction.scrap')}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                    {t('operatorDashboard.materialAction.scrapHint')}
                  </span>
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMaterialActionModal(false)}>
                {t('common:ui.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Over-BOM Confirmation Dialog */}
      {showOverBomDialog && (
        <div className="modal-overlay" onClick={() => setShowOverBomDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.overBom.title')}</h2>
              <button className="modal-close" onClick={() => setShowOverBomDialog(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginBottom: '1rem' }}>
                <strong>{t('operatorDashboard.overBom.exceedItems')}</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '1.25rem' }}>
                  {validationResults.filter(r => r.status === 'error').map(r => (
                    <li key={r.inventory_item_id} style={{ marginBottom: '4px' }}>
                      <strong>{r.item_name}</strong>{t('operatorDashboard.overBom.exceedsBy', { amount: (r.new_total - r.bom_max_quantity).toFixed(3), unit: r.unit || t('operatorDashboard.overBom.unitsFallback') })}
                    </li>
                  ))}
                </ul>
              </div>
              <p style={{ marginBottom: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.9em' }}>
                {t('operatorDashboard.overBom.reasonHint')}
              </p>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.4rem' }}>
                {t('operatorPage.fields.reason')} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <textarea
                rows="3"
                placeholder={t('operatorDashboard.overBom.reasonPlaceholder')}
                value={overBomReason}
                onChange={(e) => setOverBomReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowOverBomDialog(false)}>
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn btn-warning"
                disabled={!overBomReason.trim()}
                onClick={() => {
                  setShowOverBomDialog(false);
                  handleSubmitRequisition(true);
                }}
                style={{ opacity: overBomReason.trim() ? 1 : 0.5 }}
              >
                {t('operatorDashboard.overBom.requestAnyway')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Requisition Modal */}
      {showRequisitionModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowRequisitionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.requisitionModal.title')}</h2>
              <button className="modal-close" onClick={() => setShowRequisitionModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>{t('dialogs.completeTask.infoTask')}:</strong> {selectedTask.task_code} - {selectedTask.task_name}</p>
                {selectedTask.po_number && <p><strong>{t('labels.productionOrder')}:</strong> {selectedTask.po_number}</p>}
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {t('messages.requisitionApprovalNote')}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{t('operatorDashboard.requisitionModal.materialsRequested')}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={handleAddRequisitionItem}>
                    {t('operatorDashboard.addMaterial')}
                  </button>
                </div>

                {requisitionItems.map((item, index) => {
                  const selectedMaterial = availableMaterials.find(m => m.inventory_item_id === parseInt(item.inventory_item_id));
                  const maxQty = selectedMaterial ? parseFloat(selectedMaterial.max_quantity) : null;

                  // Find validation result for this item
                  const validationResult = validationResults.find(result =>
                    result.inventory_item_id === parseInt(item.inventory_item_id)
                  );

                  // Determine border color based on validation status
                  let borderColor = 'var(--border)';
                  if (validationResult) {
                    if (validationResult.status === 'error') borderColor = '#dc3545';
                    else if (validationResult.status === 'warning') borderColor = '#ffc107';
                    else if (validationResult.status === 'ok') borderColor = '#28a745';
                  }

                  return (
                    <div key={index}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        marginBottom: '0.25rem',
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: '4px',
                        border: `2px solid ${borderColor}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ flex: 1 }}>
                            <select
                              value={item.inventory_item_id}
                              onChange={(e) => handleRequisitionItemChange(index, 'inventory_item_id', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--border)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)'
                              }}
                            >
                              <option value="" disabled hidden>{t('operatorDashboard.selectMaterial')}</option>
                              {availableMaterials.map(mat => (
                                <option key={mat.inventory_item_id} value={mat.inventory_item_id}>
                                  {mat.item_code} - {mat.item_name}
                                </option>
                              ))}
                            </select>
                            {selectedMaterial && (
                              <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                {t('operatorDashboard.requisitionModal.bomLine', { perUnit: selectedMaterial.quantity_per_unit, orderQty: selectedMaterial.order_quantity, max: maxQty?.toFixed(3), uom: selectedMaterial.unit_of_measure })}
                                {selectedMaterial.alternative_group && (
                                  <div>{t('operatorDashboard.requisitionModal.alternativeOf', { items: availableMaterials
                                    .filter(m => m.alternative_group === selectedMaterial.alternative_group && m.inventory_item_id !== selectedMaterial.inventory_item_id)
                                    .map(m => m.item_code).join(', ') })}</div>
                                )}
                              </div>
                            )}
                          </div>
                          {requisitionItems.length > 1 && (
                            <button
                              onClick={() => handleRemoveRequisitionItem(index)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                flexShrink: 0
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
                          <input
                            type="number"
                            placeholder={t('placeholders.quantity')}
                            value={item.requested_quantity}
                            onChange={(e) => handleRequisitionItemChange(index, 'requested_quantity', e.target.value)}
                            step="0.001"
                            min="0"
                            max={maxQty || undefined}
                            style={{
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: `1px solid ${borderColor}`,
                              background: 'var(--bg-primary)',
                              color: 'var(--text-primary)'
                            }}
                          />
                          <input
                            type="text"
                            placeholder={t('placeholders.notes')}
                            value={item.notes}
                            onChange={(e) => handleRequisitionItemChange(index, 'notes', e.target.value)}
                            style={{
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid var(--border)',
                              background: 'var(--bg-primary)',
                              color: 'var(--text-primary)'
                            }}
                          />
                        </div>
                      </div>

                      {/* Validation Message */}
                      {validationResult && (
                        <div style={{
                          marginBottom: '0.5rem',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          background: validationResult.status === 'error' ? '#f8d7da' :
                                      validationResult.status === 'warning' ? '#fff3cd' : '#d4edda',
                          color: validationResult.status === 'error' ? '#721c24' :
                                 validationResult.status === 'warning' ? '#856404' : '#155724',
                          border: `1px solid ${borderColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>
                            {validationResult.status === 'error' ? '❌' :
                             validationResult.status === 'warning' ? '⚠️' : '✅'}
                          </span>
                          <div>
                            <strong>{validationResult.message}</strong>
                            {validationResult.status !== 'ok' && (
                              <div style={{ fontSize: '0.9em', marginTop: '0.25rem' }}>
                                {t('operatorDashboard.requisitionModal.bomLimits', {
                                  max: validationResult.bom_max_quantity?.toFixed(3),
                                  requested: validationResult.total_already_requested?.toFixed(3),
                                  remaining: validationResult.remaining_allowed?.toFixed(3)
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Loading indicator while validating */}
                      {validatingQuantities && item.inventory_item_id && item.requested_quantity && (
                        <div style={{
                          marginBottom: '0.5rem',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          background: '#e9ecef',
                          color: '#6c757d',
                          fontStyle: 'italic'
                        }}>
                          {t('operatorDashboard.requisitionModal.validating')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRequisitionModal(false)}>
                {t('common:ui.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSubmitRequisition}>
                {t('operatorDashboard.requisitionModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Return Modal */}
      {showReturnModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.returnModal.title')}</h2>
              <button className="modal-close" onClick={() => setShowReturnModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>{t('dialogs.completeTask.infoTask')}:</strong> {selectedTask.task_code} - {selectedTask.task_name}</p>
                {selectedTask.po_number && <p><strong>{t('labels.productionOrder')}:</strong> {selectedTask.po_number}</p>}
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {t('operatorDashboard.returnModal.hint')}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{t('operatorDashboard.returnModal.materialsToReturn')}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={handleAddReturnItem}>
                    {t('operatorDashboard.addMaterial')}
                  </button>
                </div>

                {returnItems.map((item, index) => {
                  const selectedMaterial = issuedMaterials.find(m => issuedMaterialKey(m) === item.material_key);
                  const maxQty = selectedMaterial ? parseFloat(selectedMaterial.total_issued) : null;

                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 2fr 40px',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <select
                          value={item.material_key}
                          onChange={(e) => handleReturnItemChange(index, 'material_key', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          <option value="" disabled hidden>{t('operatorDashboard.selectMaterial')}</option>
                          {issuedMaterials.map(mat => (
                            <option key={issuedMaterialKey(mat)} value={issuedMaterialKey(mat)}>
                              {mat.item_code} - {mat.item_name} ({mat.requisition_number})
                            </option>
                          ))}
                        </select>
                        {selectedMaterial && (
                          <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {t('operatorDashboard.issuedQty', { qty: parseFloat(selectedMaterial.total_issued).toFixed(3), uom: selectedMaterial.unit_of_measure })}
                          </div>
                        )}
                      </div>

                      <input
                        type="number"
                        placeholder={t('placeholders.quantity')}
                        value={item.quantity_returned}
                        onChange={(e) => handleReturnItemChange(index, 'quantity_returned', e.target.value)}
                        step="0.001"
                        min="0"
                        max={maxQty || undefined}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      <input
                        type="text"
                        placeholder={t('placeholders.reason')}
                        value={item.reason}
                        onChange={(e) => handleReturnItemChange(index, 'reason', e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      {returnItems.length > 1 && (
                        <button
                          onClick={() => handleRemoveReturnItem(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            padding: '0.5rem'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReturnModal(false)}>
                {t('common:ui.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSubmitReturn} disabled={submittingReturn}>
                {submittingReturn ? t('operatorDashboard.returnModal.submitting') : t('operatorDashboard.returnModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scrap Recording Modal */}
      {showScrapModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowScrapModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{t('operatorDashboard.scrapModal.title')}</h2>
              <button className="modal-close" onClick={() => setShowScrapModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#FFF3CD', borderRadius: '8px', border: '1px solid #FFE69C' }}>
                <p style={{ margin: 0, color: '#856404' }}>
                  ⚠️ <strong>{t('operatorDashboard.scrapModal.warningTitle')}</strong> {t('operatorDashboard.scrapModal.warningBody')}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p><strong>{t('dialogs.completeTask.infoTask')}:</strong> {selectedTask.task_code} - {selectedTask.task_name}</p>
                {selectedTask.po_number && <p><strong>{t('labels.productionOrder')}:</strong> {selectedTask.po_number}</p>}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{t('operatorDashboard.scrapModal.materialsToScrap')}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={handleAddScrapItem}>
                    {t('operatorDashboard.addMaterial')}
                  </button>
                </div>

                {scrapItems.map((item, index) => {
                  const selectedMaterial = issuedMaterials.find(m => m.inventory_item_id === parseInt(item.inventory_item_id));

                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 2fr 40px',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <select
                          value={item.inventory_item_id}
                          onChange={(e) => handleScrapItemChange(index, 'inventory_item_id', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          <option value="" disabled hidden>{t('operatorDashboard.selectMaterial')}</option>
                          {issuedMaterials.map(mat => (
                            <option key={mat.inventory_item_id} value={mat.inventory_item_id}>
                              {mat.item_code} - {mat.item_name}
                            </option>
                          ))}
                        </select>
                        {selectedMaterial && (
                          <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {t('operatorDashboard.issuedQty', { qty: parseFloat(selectedMaterial.total_issued).toFixed(3), uom: selectedMaterial.unit_of_measure })}
                          </div>
                        )}
                      </div>

                      <input
                        type="number"
                        placeholder={t('placeholders.quantity')}
                        value={item.scrap_quantity}
                        onChange={(e) => handleScrapItemChange(index, 'scrap_quantity', e.target.value)}
                        step="0.001"
                        min="0"
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      <select
                        value={item.scrap_reason}
                        onChange={(e) => handleScrapItemChange(index, 'scrap_reason', e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        <option value="" disabled hidden>{t('operatorDashboard.scrapModal.selectReason')}</option>
                        {['DAMAGE', 'PRODUCTION_DEFECT', 'EXPIRED', 'CONTAMINATED', 'SPILLAGE', 'OTHER'].map(reason => (
                          <option key={reason} value={reason}>{t(`operatorDashboard.scrapModal.reasons.${reason}`)}</option>
                        ))}
                      </select>

                      {scrapItems.length > 1 && (
                        <button
                          onClick={() => handleRemoveScrapItem(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            padding: '0.5rem'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowScrapModal(false)}>
                {t('common:ui.cancel')}
              </button>
              <button className="btn btn-danger" onClick={handleSubmitScrap} style={{ background: '#dc3545' }}>
                {t('operatorDashboard.scrapModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={() => {
            // Store the callback before clearing state
            const callback = confirmDialog.onConfirm;
            // Close dialog first
            setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
            // Then call the callback after a brief delay to ensure dialog unmounts
            setTimeout(() => {
              if (callback && typeof callback === 'function') {
                callback();
              }
            }, 50);
          }}
          onCancel={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
          confirmText={t('common:ui.confirm')}
          cancelText={t('common:ui.cancel')}
          confirmStyle="primary"
        />
      )}

      {/* Prompt Dialog */}
      {promptDialog.show && (
        <PromptDialog
          title={promptDialog.title}
          message={promptDialog.message}
          fields={promptDialog.fields || [{ name: 'value', label: t('operatorDashboard.promptValue'), type: 'text' }]}
          onSubmit={(value) => {
            // Store the callback before clearing state
            const callback = promptDialog.onSubmit;
            // Close dialog first
            setPromptDialog({ show: false, title: '', message: '', fields: [], onSubmit: null });
            // Then call the callback after a brief delay to ensure dialog unmounts
            setTimeout(() => {
              if (callback && typeof callback === 'function') {
                callback(value);
              }
            }, 50);
          }}
          onCancel={() => setPromptDialog({ show: false, title: '', message: '', fields: [], onSubmit: null })}
          submitText={t('buttons.submit')}
          cancelText={t('common:ui.cancel')}
        />
      )}
    </div>

      <ShiftHandoffModal
        isOpen={showShiftHandoffModal}
        onClose={() => setShowShiftHandoffModal(false)}
        taskId={selectedTask?.task_id}
        taskType={selectedTask?.task_type}
        onHandoffComplete={() => {
          setShowShiftHandoffModal(false);
          fetchOperatorDashboard();
        }}
      />
    </>
  );
};

export default OperatorDashboard;